import {
  trackAiVoiceSampleListenCompleted,
  trackAiVoiceSampleListenFailed,
  trackAiVoiceSampleListenStarted,
} from '@src/analytics/EventTracker';
import {
  MODAL_TOOLTIP_STYLE,
  PARAGRAPH_TOOLTIP_STYLE,
} from '@src/components/common/tooltipStyles';
import { useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import CloseIcon from '@src/svgs/CloseIcon';
import { MuxPlayIcon } from '@src/svgs/MuxPlayIcon';
import { extractErrorInfo, isAbortError } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { observer } from 'mobx-react-lite';
import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Tooltip } from 'react-tooltip';
import styled, { css } from 'styled-components';

import AudioSamplePlayer from './AudioSamplePlayer';
import { languageGroups } from './languageGroups';
import { SamplePlayGlyph } from './samplePlayStyles';
import VoiceAvatar from './VoiceAvatar';
import { providerLabel } from './voiceLabels';

import type { AiVoiceRecord } from '@src/types.ts/aiVoiceRecord';
import type { Language } from '@src/types.ts/language';
import type { CSSProperties } from 'react';

const logger = LogManager.getLogger('aiVoices:VoiceInfoCard');

// Below this many languages a filter box is noise: the list is scannable at a
// glance and the box would sit beside a count of one or two.
const LANGUAGE_FILTER_MIN = 6;

// The full description on hover: the design system's paragraph tooltip token,
// lifted to the modal tooltip layer because this card renders inside modals.
// Opaque — the default react-tooltip fade let the rows beneath show through.
const DESCRIPTION_TOOLTIP_STYLE: CSSProperties = {
  ...PARAGRAPH_TOOLTIP_STYLE,
  zIndex: MODAL_TOOLTIP_STYLE.zIndex,
};

// The presentation card for a single AI Voice (DE10 shape, scoped to the
// audition sub-section; PLAT-10576 / T15b). Shows the voice's avatar, name,
// provider, description and a table of ALL the languages it supports (workspace
// languages flagged + first, DI19). Clicking a language plays that voice's
// pre-saved per-language sample (DE8) via the shared AudioSamplePlayer. Sample
// availability is known up-front from the record's `voiceSamples` map, so a
// language with no stored sample is disabled (grayed play) — never clickable
// into an error and never a live synth.
interface VoiceInfoCardProps {
  voice: AiVoiceRecord;
  /**
   * Language `value`s for which this voice is the speaker's resolved default.
   * When set, those rows are badged "Default" (speaker pop-over only; Settings
   * passes nothing).
   */
  defaultForLanguageValues?: Set<string>;
  /**
   * `card` (default): the self-contained bordered card Settings and the
   * speaker pop-over show. `detail` (PLAT-11581): the quiet form for a pane
   * that sits beside a primary list — no chrome of its own, a one-line
   * name-and-provider anchor instead of the header, ghost play buttons and
   * note-tag flags, so the list stays the thing the eye lands on.
   */
  variant?: 'card' | 'detail';
}

type SampleStatus = 'loading' | 'ready' | 'unavailable' | 'error';

interface ActiveSample {
  language: Language;
  status: SampleStatus;
  url?: string;
  error?: string;
}

const VoiceInfoCard = observer(
  ({
    voice,
    defaultForLanguageValues,
    variant = 'card',
  }: VoiceInfoCardProps) => {
    const { datoClientStore } = useStore();

    // The description is clamped to two lines; the tooltip with the full text
    // only exists when the clamp actually cut something (PLAT-11581). Measured
    // after layout and re-measured when the pane resizes.
    const descriptionRef = useRef<HTMLParagraphElement>(null);
    const [descriptionClamped, setDescriptionClamped] = useState(false);
    useLayoutEffect(() => {
      const el = descriptionRef.current;
      if (!el) {
        setDescriptionClamped(false);
        return undefined;
      }
      const measure = () =>
        setDescriptionClamped(el.scrollHeight > el.clientHeight + 1);
      measure();
      if (typeof ResizeObserver === 'undefined') return undefined;
      const observer = new ResizeObserver(measure);
      observer.observe(el);
      return () => observer.disconnect();
    }, [voice.description, variant]);
    const videoCreator = useVideoCreatorStore();
    const descriptionTooltipId = useId();
    const workspaceLanguages = useMemo(
      () => videoCreator.organization?.supportedLanguages ?? [],
      [videoCreator.organization?.supportedLanguages],
    );

    const { workspaceGroup, otherGroup } = useMemo(
      () => languageGroups(voice.supportedLanguages, workspaceLanguages),
      [voice.supportedLanguages, workspaceLanguages],
    );

    // A language is flagged "In workspace" when it's actually one of the
    // workspace's languages (the always-appended English fallback isn't, unless
    // the workspace lists it).
    const workspaceValues = useMemo(
      () => new Set(workspaceLanguages.map(l => l.value)),
      [workspaceLanguages],
    );

    const [active, setActive] = useState<ActiveSample | null>(null);
    const [query, setQuery] = useState('');
    const abortRef = useRef<AbortController | null>(null);

    const totalLanguages = workspaceGroup.length + otherGroup.length;
    const showFilter = totalLanguages >= LANGUAGE_FILTER_MIN;

    // Filter both groups by caption (case-insensitive), preserving the
    // workspace-first ordering. A query typed for a long list is ignored (not
    // cleared) while a shorter voice hides the box, so it comes back intact.
    const { filteredWorkspace, filteredOther } = useMemo(() => {
      const q = showFilter ? query.trim().toLowerCase() : '';
      if (!q)
        return { filteredWorkspace: workspaceGroup, filteredOther: otherGroup };
      const matches = (l: Language) => l.caption.toLowerCase().includes(q);
      return {
        filteredWorkspace: workspaceGroup.filter(matches),
        filteredOther: otherGroup.filter(matches),
      };
    }, [query, showFilter, workspaceGroup, otherGroup]);
    const hasMatches = filteredWorkspace.length + filteredOther.length > 0;

    // A new voice clears any playing sample and cancels an in-flight fetch. The
    // language filter is intentionally kept so switching voices doesn't wipe what
    // the user typed (they're usually comparing the same language across voices).
    useEffect(() => {
      abortRef.current?.abort();
      abortRef.current = null;
      setActive(null);
    }, [voice.id]);

    // Cancel any in-flight sample fetch on unmount.
    useEffect(() => () => abortRef.current?.abort(), []);

    const handleSample = async (language: Language) => {
      // Selecting another language supersedes any in-flight fetch.
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setActive({ language, status: 'loading' });
      const analyticsBase = {
        ai_voice_id: voice.id,
        provider: voice.provider,
        language: language.value,
      };
      trackAiVoiceSampleListenStarted(analyticsBase);

      try {
        const result =
          await datoClientStore.dubbingRepository.getVoiceSampleUrl({
            aiVoiceId: voice.id,
            language: language.value,
            signal: controller.signal,
          });
        if (controller.signal.aborted) return;
        if (!result) {
          // The row is only enabled when the record lists a sample key for this
          // language, so a null URL means the backend couldn't resolve it (not
          // yet served / inconsistency). Surface it and count it as a failed
          // listen — never a silent no-op, and never an orphan Started event.
          setActive({ language, status: 'unavailable' });
          logger.warn('sample url unavailable despite a listed sample key', {
            aiVoiceId: voice.id,
            language: language.value,
          });
          trackAiVoiceSampleListenFailed({
            ...analyticsBase,
            error_message: 'sample_unavailable',
          });
          return;
        }
        setActive({ language, status: 'ready', url: result.audioUrl });
        trackAiVoiceSampleListenCompleted({
          ...analyticsBase,
          duration_ms: result.durationMs ?? 0,
        });
      } catch (err) {
        if (isAbortError(err)) return;
        const info = extractErrorInfo(err);
        const message = info?.errorMessage || 'Couldn’t load the sample.';
        setActive({ language, status: 'error', error: message });
        logger.error('sample fetch failed', info);
        trackAiVoiceSampleListenFailed({
          ...analyticsBase,
          error_message: message,
        });
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
      }
    };

    const renderRow = (language: Language, inWorkspace: boolean) => {
      const isActive = active?.language.value === language.value;
      // Availability is known up-front from the record's sample map — no fetch
      // needed. A language with no stored sample is disabled (grayed play) rather
      // than clickable-into-an-error.
      const available = Boolean(voice.voiceSamples?.[language.value]);
      const isDefault = defaultForLanguageValues?.has(language.value) ?? false;
      return (
        <LanguageRow
          key={language.id}
          type="button"
          $active={isActive}
          disabled={!available}
          title={
            available ? undefined : 'No sample available for this language'
          }
          onClick={() => handleSample(language)}
        >
          <span className="caption">{language.caption}</span>
          <span className="trailing">
            {isDefault && (
              <span
                className="default-flag"
                title="This voice is this speaker’s default for this language"
              >
                Default
              </span>
            )}
            {inWorkspace && <span className="flag">In workspace</span>}
            {isActive && active?.status === 'loading' ? (
              <span className="status-text">…</span>
            ) : isActive && active?.status === 'unavailable' ? (
              <span className="status-text">Unavailable</span>
            ) : isActive && active?.status === 'error' ? (
              <span className="status-text" title={active.error}>
                Error
              </span>
            ) : (
              <SamplePlayGlyph
                $size={variant === 'detail' ? 24 : 28}
                $disabled={!available}
                aria-hidden
              >
                <MuxPlayIcon width="11" height="11" fillColor="currentColor" />
              </SamplePlayGlyph>
            )}
          </span>
        </LanguageRow>
      );
    };

    const detail = variant === 'detail';
    return (
      <Card $detail={detail}>
        {detail ? (
          <DetailAnchor>
            <span className="name">{voice.name}</span>
            {' · '}
            {providerLabel(voice.provider)}
          </DetailAnchor>
        ) : (
          <header>
            <VoiceAvatar voice={voice} size={48} />
            <div className="identity">
              <h4>{voice.name}</h4>
              <span className="provider">{providerLabel(voice.provider)}</span>
            </div>
          </header>
        )}

        {voice.description && (
          <>
            <p
              ref={descriptionRef}
              className="description"
              data-tooltip-id={
                descriptionClamped ? descriptionTooltipId : undefined
              }
              data-tooltip-content={
                descriptionClamped ? voice.description : undefined
              }
            >
              {voice.description}
            </p>
            <Tooltip
              id={descriptionTooltipId}
              style={DESCRIPTION_TOOLTIP_STYLE}
              opacity={1}
            />
          </>
        )}

        <LanguageSection>
          <div className="section-head">
            <span className="section-label">Languages</span>
            <span className="count">{totalLanguages}</span>
            {showFilter && (
              <input
                className="search"
                type="text"
                value={query}
                placeholder="Filter languages…"
                aria-label="Filter languages"
                onChange={e => setQuery(e.target.value)}
              />
            )}
          </div>
          <div className="language-list">
            {filteredWorkspace.map(l =>
              renderRow(l, workspaceValues.has(l.value)),
            )}
            {filteredOther.map(l => renderRow(l, false))}
            {!hasMatches && (
              <div className="empty">No languages match “{query.trim()}”.</div>
            )}
          </div>
        </LanguageSection>

        {active?.status === 'ready' && active.url && (
          <SamplePlayback>
            <span className="playing-label">
              Sample · {active.language.caption}
            </span>
            <div className="player-row">
              <AudioSamplePlayer url={active.url} autoPlay />
              <button
                type="button"
                className="close-sample"
                aria-label="Close preview"
                title="Close preview"
                onClick={() => setActive(null)}
              >
                <CloseIcon width={14} height={14} />
              </button>
            </div>
          </SamplePlayback>
        )}
      </Card>
    );
  },
);

export default VoiceInfoCard;

const Card = styled.div<{ $detail: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--surface);
  min-width: 0;
  /* Absolutely fill the relative CardCell so the card never inflates the grid
     row: its height is driven by the left column, and the language list scrolls
     inside rather than growing the card. Normal flow on the single-column
     (mobile) layout. */
  position: absolute;
  inset: 0;
  min-height: 0;
  overflow: hidden;

  @media (max-width: 900px) {
    position: static;
  }

  > header {
    display: flex;
    align-items: center;
    gap: 12px;

    .identity {
      display: flex;
      flex-direction: column;
      gap: 2px;
      min-width: 0;
    }

    h4 {
      margin: 0;
      font-size: var(--text-base);
      font-weight: var(--font-weight-bold);
      color: var(--foreground);
    }

    .provider {
      font-size: var(--text-xs);
      color: var(--text-muted);
    }
  }

  .description {
    margin: 0;
    font-size: var(--text-sm);
    line-height: 1.5;
    color: var(--text-secondary);
    /* Clamp to two lines; full text on hover (react-tooltip). */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  /* Detail variant (PLAT-11581): a pane beside a primary list, not a card. No
     border or fill of its own, one step smaller everywhere, and no green until
     the pointer asks for it. Doubled selectors beat the nested components'
     own class rules. */
  ${p =>
    p.$detail &&
    css`
      padding: 0 0 0 4px;
      border: none;
      background: transparent;
      border-radius: 0;

      /* Type scale inside the pane: one step below the list. */
      && .description {
        font-size: var(--text-xs);
        color: var(--text-secondary);
        line-height: 1.5;
      }
      && .section-label {
        font-size: var(--text-xs);
        font-weight: var(--font-weight-semibold);
        color: var(--text-muted);
      }
      && .search {
        font-size: var(--text-xs);
        padding: 4px 8px;
      }
      && .language-list {
        border: none;
        border-radius: 0;
      }
      && .language-list > button {
        font-size: var(--text-xs);
        padding: 6px 8px;
      }
      && .flag {
        padding: 0;
        background: transparent;
        color: var(--text-muted);
        font-weight: var(--font-weight-normal);
      }
    `}
`;

/* The pane's only anchor to the selected row: one muted line, the name in the
   same gold as the row. Tile, eyebrow and a separate provider line would only
   repeat what the row already shows. */
const DetailAnchor = styled.p`
  margin: 0;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
  line-height: 1.4;

  .name {
    color: var(--accent);
  }
`;

const LanguageSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  /* Grow to fill the card and let the list scroll within. */
  flex: 1;
  min-height: 0;

  .section-head {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .section-head .search {
    flex: 1;
    min-width: 0;
    margin-left: 8px;
  }

  .section-label {
    font-size: var(--text-sm);
    font-weight: var(--font-weight-semibold);
    color: var(--foreground);
  }

  .count {
    padding: 0 8px;
    border-radius: 999px;
    background: var(--surface-elevated);
    color: var(--text-secondary);
    font-size: var(--text-xs);
    font-weight: var(--font-weight-semibold);
    font-variant-numeric: tabular-nums;
  }

  .search {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid var(--border);
    border-radius: 8px;
    background: transparent;
    color: var(--foreground);
    font-size: var(--text-sm);
    font-family: var(--font-primary);
    transition: var(--transition-normal);

    &:focus {
      outline: none;
      border-color: var(--primary);
    }
  }

  /* Sized to its rows; shrinks and scrolls only when the card runs out of
     room, so one language never sits above an empty bordered block. */
  .language-list {
    display: flex;
    flex-direction: column;
    flex: 0 1 auto;
    min-height: 0;
    overflow-y: auto;
    border: 1px solid var(--border);
    border-radius: 8px;
  }

  .empty {
    padding: 16px 12px;
    color: var(--text-muted);
    font-size: var(--text-sm);
  }
`;

const LanguageRow = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-bottom: 1px solid var(--border);
  background: ${p => (p.$active ? 'var(--surface-elevated)' : 'transparent')};
  color: var(--foreground);
  font-size: var(--text-sm);
  font-family: var(--font-primary);
  text-align: left;
  cursor: pointer;
  transition: var(--transition-normal);

  &:last-child {
    border-bottom: none;
  }
  &:hover:not(:disabled) {
    background: var(--surface-elevated);
  }
  &:disabled {
    cursor: not-allowed;
  }

  .caption {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* Everything on the right, flush right: the "In workspace" flag then the
     play control. */
  .trailing {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
    margin-left: auto;
  }

  /* A status, not an action: soft neutral fill, never green. */
  .flag {
    flex-shrink: 0;
    padding: 1px 6px;
    border-radius: 999px;
    background: var(--surface-elevated);
    color: var(--text-secondary);
    font-size: var(--text-xs);
    font-weight: var(--font-weight-medium);
  }

  /* Marks the language this voice is the speaker's default for (gold to set it
     apart from the green "In workspace" flag). */
  .default-flag {
    flex-shrink: 0;
    padding: 1px 6px;
    border-radius: 999px;
    background: color-mix(in srgb, var(--accent) 20%, transparent);
    color: var(--accent);
    font-size: var(--text-xs);
    font-weight: var(--font-weight-semibold);
  }

  .status-text {
    color: var(--text-muted);
    font-size: var(--text-xs);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  /* The row is the button; its hover lights the shared play glyph. */
  &:hover:not(:disabled) ${SamplePlayGlyph} {
    background: var(--surface-elevated);
    color: var(--foreground);
  }
`;

const SamplePlayback = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  .playing-label {
    font-size: var(--text-xs);
    color: var(--text-muted);
  }

  .player-row {
    display: flex;
    align-items: center;
    gap: 8px;

    > *:first-child {
      flex: 1;
      min-width: 0;
    }
  }

  .close-sample {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 1px solid var(--border);
    border-radius: 6px;
    background: transparent;
    color: var(--text-muted);
    cursor: pointer;
    transition: var(--transition-normal);

    &:hover {
      border-color: var(--primary);
      color: var(--foreground);
    }
  }
`;
